import {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useSelector} from 'react-redux'
import {Popper, Paper, ClickAwayListener, InputAdornment, Divider, Tooltip, useTheme, Typography} from '@mui/material'
import {FocusTrap} from '@mui/base/FocusTrap'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  getPopoverPaperStyles,
  PopoverContainer,
  PopoverHeader,
  PopoverTitle,
  CloseButton,
  StyledCategoryDescription,
  SearchContainer,
  SearchTextField,
  FilterListContainer,
  FilterItemContainer,
  FilterFormControlLabel,
  StyledFilterLabelContainer,
  StyledInfoIconButton,
  NoFiltersMessage,
  FooterContainer,
  FooterMessage
} from './FilterPopoverStyles'
import {FilterCheckbox} from '../../common/FilterCheckbox/FilterCheckbox'
import {useFilterManagement} from '../../../store/hooks/useFilterManagement'
import {useFilterBusinessLogic} from '../../../store/hooks/useFilterBusinessLogic'
import {selectAvailableFilters} from '../../../store/slices/filterManagementSlice'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

/**
 * FilterPopover Component
 *
 * Displays a popover with searchable filter checkboxes.
 * Integrated with three-level filter management system with business logic support.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the popover is open
 * @param {HTMLElement} props.anchorEl - Anchor element for popover positioning
 * @param {Function} props.onClose - Callback when popover is closed
 * @param {string} props.categoryTitle - Title of the category (may be customized)
 * @param {string} props.originalCategoryTitle - Original title of the category (for analytics)
 * @param {string} props.categoryDesc - Description of the category
 * @param {string} props.footerMessage - Optional footer message to display
 * @param {Array} props.filters - Array of filter objects from config
 * @param {string} props.categoryId - Category ID for filtering
 */
const FilterPopover = ({
  open,
  anchorEl,
  onClose,
  categoryTitle,
  originalCategoryTitle,
  categoryDesc,
  footerMessage,
  filters = [],
  categoryId
}) => {
  const theme = useTheme()
  const [searchQuery, setSearchQuery] = useState('')

  // Get anchor element's bounding rect for dynamic height calculation
  // Compute fresh on every render so rect is never stale after scroll/layout changes
  const anchorRect = anchorEl ? anchorEl.getBoundingClientRect() : null

  // Use filter management hook
  const {isFilterAdded, toggleFilter, addMultipleFilters, removeFilter} = useFilterManagement()

  // Use business logic hook for disabled state and reasons
  const {isFilterDisabled, getDisabledReason} = useFilterBusinessLogic()

  // Get available filter keys directly from Redux (more stable than function reference)
  const availableFilterKeys = useSelector(selectAvailableFilters)

  // Filter based on availability (permissions)
  const availableFilters = useMemo(() => {
    return filters.filter((filter) => {
      // If no permissions loaded yet, show all filters
      if (availableFilterKeys.length === 0) return true
      // Show if available (has permission)
      return availableFilterKeys.includes(filter.key)
    })
  }, [filters, availableFilterKeys])

  // Filter based on search query
  const filteredFilters = useMemo(() => {
    return availableFilters.filter((filter) => filter.name.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [availableFilters, searchQuery])

  // Get only filters that are not disabled
  const selectableFilters = useMemo(() => {
    return availableFilters.filter((filter) => !isFilterDisabled(filter.key))
  }, [availableFilters, isFilterDisabled])

  // Check if all selectable (non-disabled) filters are added
  const allSelected = selectableFilters.length > 0 && selectableFilters.every((filter) => isFilterAdded(filter.key))

  const handleSelectAll = () => {
    if (allSelected) {
      for (const filter of selectableFilters) {
        if (isFilterAdded(filter.key)) {
          removeFilter(filter.key)
        }
      }
    } else {
      const filtersToAdd = selectableFilters.filter((filter) => !isFilterAdded(filter.key))
      addMultipleFilters(filtersToAdd.map((filter) => filter.key))

      // Skip analytics for Outreach Simulation filters (categoryId === '8')
      // Analytics will be sent when clicking individual options like SNAP in the filter control
      if (categoryId !== '8') {
        for (const filter of filtersToAdd) {
          setTimeout(() => {
            AnalyticsHandler.onFilterApply({
              name: filter.name, // Use original name, not customName
              category: originalCategoryTitle || categoryTitle // Use original category title for analytics
            })
          }, 50)
        }
      }
    }
  }

  const handleFilterToggle = (filter) => () => {
    const isApplying = !isFilterAdded(filter.key)
    toggleFilter(filter.key)

    // Skip analytics for Outreach Simulation filters (categoryId === '8')
    // Analytics will be sent when clicking individual options like SNAP in the filter control
    if (isApplying && categoryId !== '8') {
      AnalyticsHandler.onFilterApply({
        name: filter.name, // Use original name, not customName
        category: originalCategoryTitle || categoryTitle // Use original category title for analytics
      })
    }
  }

  const handleClose = () => {
    setSearchQuery('')
    onClose()
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement='right-start'
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [60, 0]
          }
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 16
          }
        }
      ]}
      style={{zIndex: theme.zIndex.modal}}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper sx={getPopoverPaperStyles(theme, anchorRect)}>
          <FocusTrap open={open} disableEnforceFocus>
            <PopoverContainer>
              {/* Header with title and close button */}
              <PopoverHeader>
                <PopoverTitle variant='subtitle1'>{categoryTitle}</PopoverTitle>
                <CloseButton size='small' onClick={handleClose} aria-label='Close'>
                  <CloseIcon fontSize='small' />
                </CloseButton>
              </PopoverHeader>

              {/* Category Description */}
              {categoryDesc && <StyledCategoryDescription variant='body2'>{categoryDesc}</StyledCategoryDescription>}

              {/* Search box */}
              <SearchContainer>
                <SearchTextField
                  fullWidth
                  size='small'
                  placeholder='Search'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' />
                      </InputAdornment>
                    )
                  }}
                />
              </SearchContainer>

              {/* Filter list */}
              <FilterListContainer>
                {/* Select All option */}
                {availableFilters.length > 0 && (
                  <FilterFormControlLabel
                    control={<FilterCheckbox size='small' checked={allSelected} onChange={handleSelectAll} />}
                    label={<Typography variant='subtitle1'>Select all</Typography>}
                  />
                )}

                {/* Individual filter checkboxes */}
                {filteredFilters.length > 0 ? (
                  filteredFilters.map((filter) => {
                    const disabled = isFilterDisabled(filter.key)
                    const disabledReason = getDisabledReason(filter.key)
                    return (
                      <FilterItemContainer key={filter.key}>
                        <Tooltip
                          title={disabledReason || ''}
                          placement='top'
                          arrow
                          disableHoverListener={!disabled}
                          disableFocusListener={!disabled}
                        >
                          <div>
                            <FilterFormControlLabel
                              control={
                                <FilterCheckbox
                                  size='small'
                                  checked={isFilterAdded(filter.key)}
                                  onChange={handleFilterToggle(filter)}
                                  disabled={disabled}
                                />
                              }
                              label={
                                <StyledFilterLabelContainer>
                                  <Typography variant='subtitle1' sx={{opacity: disabled ? 0.5 : 1}}>
                                    {filter.customName || filter.name}
                                  </Typography>
                                  {/* Only show description tooltip if name is NOT customized */}
                                  {filter.desc && !filter.customName && (
                                    <Tooltip title={filter.desc} placement='top'>
                                      <StyledInfoIconButton size='small' aria-label={`Info about ${filter.name}`}>
                                        <InfoOutlinedIcon fontSize='small' />
                                      </StyledInfoIconButton>
                                    </Tooltip>
                                  )}
                                </StyledFilterLabelContainer>
                              }
                            />
                          </div>
                        </Tooltip>
                      </FilterItemContainer>
                    )
                  })
                ) : (
                  <NoFiltersMessage variant='body2'>
                    {searchQuery ? 'No filters found' : 'No available filters'}
                  </NoFiltersMessage>
                )}
              </FilterListContainer>

              {/* Footer Message */}
              {footerMessage && (
                <>
                  <Divider />
                  <FooterContainer>
                    <FooterMessage variant='subtitle1'>
                      <strong>Note:</strong> {footerMessage}
                    </FooterMessage>
                  </FooterContainer>
                </>
              )}
            </PopoverContainer>
          </FocusTrap>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

FilterPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  categoryTitle: PropTypes.string.isRequired,
  originalCategoryTitle: PropTypes.string,
  categoryDesc: PropTypes.string,
  footerMessage: PropTypes.string,
  categoryId: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      desc: PropTypes.string,
      type: PropTypes.string
    })
  )
}

export default FilterPopover
